// [VERIFY-3i] Tabelas de lookup e formatadores PUROS do card de decisao do
// Modo Agente (US-8 — spec-002), extraidos de `ia-confirm-action.vue` pra
// manter o componente <= 500 linhas (regra HARD do repo, coding standards
// do CLAUDE.md raiz). Nada aqui depende de Vue/props/estado de instancia —
// so recebe primitivos e devolve string/objeto, mesmo padrao de
// `run-status-chip.util.js`.
import CONTRATO_DE_ROTULOS from '@modules/aawz-intelligence-v2/ui-registry/contratos/entidade-labels.json'
import { aawzDate } from '@utils/dates/aawz-date'

// ⚠️ Estas tabelas eram TRÊS entidades e QUATRO operações redigitadas aqui,
// ao lado do contrato de 30 que o `ia-confirm-action.vue` já importava —
// duas listas para o mesmo fato. Quem pagava era o RECIBO: o único consumidor
// real do util fora dos testes é `resolverReciboEntidadeLabel` (pelo
// `ia-confirm-action-decisao.mixin.js`), e ele devolvia
// "Financial_planning_objetivos" onde o título do mesmo cartão já dizia
// "Objetivo financeiro". Agora é a MESMA cópia byte a byte do backend
// (`contratos/entidade-labels.json`) — o fallback que o schema declara.
export const ENTIDADE_LABELS = CONTRATO_DE_ROTULOS.entidades
export const OPERACAO_LABELS = CONTRATO_DE_ROTULOS.operacoes

// Os valores de `estadoVisual` do card (a proposta vista pela tela, não o
// estado cru do servidor).
export const ESTADO_VISUAL = Object.freeze({
  ABERTA: 'aberta',
  ENVIANDO: 'enviando',
  COMMITADA: 'commitada',
  REJEITADA: 'rejeitada',
  EXPIRADA: 'expirada',
  CANCELADA: 'cancelada',
  INVALIDADA: 'invalidada',
  ERRO: 'erro',
})

// Estados terminais da proposta (server-truth) equivalentes a "rejeitada" —
// usados por `estadoVisual` (ver reconciliacao de nomenclatura no cabecalho
// do componente: ui-components.md usa 'decidida', o wireframe mostra
// "Rejeitada").
export const ESTADOS_REJEITADA = ['rejeitada', 'decidida']

// ── O TOM da faixa (19/09/2026, redesenho do owner) ─────────────────────────
//
// O ponto de cor à esquerda substituiu o ícone grande e a faixa cinza de
// cabeçalho. O TOM é semântico de propósito: quem mapeia tom → token é o
// `<style>` do componente, então trocar a cor de um grupo é uma linha lá e
// nenhuma aqui. (Foi o que custou caro no `run-status-chip`, onde a cor
// morava espalhada na tabela de estados.)
export const TOM = Object.freeze({
  AGUARDANDO: 'aguardando',
  GRAVADO: 'gravado',
  ENCERRADO: 'encerrado',
  FALHOU: 'falhou',
})

// ⚠️ `expirada` é ENCERRADO, não FALHOU — mesma correção que o
// `run-status-chip` fez em VERIFY-3f ao tirar `expired` de `danger`: "terminou
// sem executar" não é "deu erro", e pintar os dois de vermelho apaga a
// diferença justamente para quem precisa dela. `danger` fica exclusivo do
// estado `erro`.
const TOM_POR_ESTADO_VISUAL = {
  aberta: TOM.AGUARDANDO,
  enviando: TOM.AGUARDANDO,
  commitada: TOM.GRAVADO,
  rejeitada: TOM.ENCERRADO,
  cancelada: TOM.ENCERRADO,
  invalidada: TOM.ENCERRADO,
  expirada: TOM.ENCERRADO,
  erro: TOM.FALHOU,
}

/** O tom do ponto por `estadoVisual`; estado fora do mapa continua aguardando. */
export function resolverTom(estadoVisual) {
  return TOM_POR_ESTADO_VISUAL[estadoVisual] || TOM.AGUARDANDO
}

export function resolverEntidadeLabel(entidade) {
  return ENTIDADE_LABELS[entidade] || 'Registro'
}

export function resolverOperacaoLabel(operacao) {
  return OPERACAO_LABELS[operacao] || 'Confirmar'
}

export function capitalizar(texto) {
  if (!texto) return ''
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

/**
 * Caixa de frase no meio do título ("Criar" + "Tarefa" → "Criar tarefa").
 *
 * ★ [owner, 19/09/2026] Mesma virada que o `run-status-chip` fez nos rótulos
 * dele. Os dois pedaços vêm do backend já capitalizados, cada um por si; unidos
 * viravam "Criar Tarefa", que lê como nome próprio.
 *
 * ⚠️ Acrônimo fica como está: "CRM" com a inicial baixada viraria "cRM". O
 * teste é a SEGUNDA letra — só quem tem minúscula depois da inicial é palavra
 * comum.
 */
export function minusculaInicial(texto) {
  if (!texto) return ''
  const segunda = texto.charAt(1)
  const ehAcronimo = segunda && segunda !== segunda.toLowerCase()
  if (ehAcronimo) return texto
  return texto.charAt(0).toLowerCase() + texto.slice(1)
}

export function resolverReciboEntidadeLabel(entidade) {
  if (!entidade) return ''
  return ENTIDADE_LABELS[entidade] || capitalizar(entidade)
}

export function formatarReciboHora(commitadoEm) {
  if (!commitadoEm) return ''
  const data = aawzDate(commitadoEm)
  return data.isValid() ? data.format('HH:mm') : ''
}

// [VERIFY-3/Q-6] `id_registro` de entidades com PK uuid (ex.: `pm_task`) e
// ilegivel por inteiro no card ("Tarefa #e957f079-5aad-4c87-..."); o
// wireframe W1 previa a forma curta ("tarefa #4412"). Ids curtos
// (sequenciais/numericos) passam direto — so uuid (> 8 chars) e cortado.
export function formatarIdRegistroCurto(idRegistro) {
  if (!idRegistro) return ''
  const idStr = String(idRegistro)
  return idStr.length > 8 ? idStr.slice(0, 8) : idStr
}

export function montarTextoCommitada({ reciboHoraFormatada, reciboEntidadeLabel, reciboIdRegistroCurto }) {
  if (!reciboHoraFormatada) return 'Gravado.'
  let texto = 'Gravado às ' + reciboHoraFormatada
  if (reciboEntidadeLabel) {
    texto += ' · ' + reciboEntidadeLabel
    if (reciboIdRegistroCurto) {
      texto += ' #' + reciboIdRegistroCurto
    }
  }
  return texto
}

// ── Resumo "Rótulo: valor · Rótulo: valor" do preview ──────────────────────
// O backend (`write_audit_engine`) já escreve assim; nenhum rótulo é inventado.
const SEPARADOR_DO_RESUMO = '·'
// Só "Prazo" é data por contrato: ganha relativo e mostra o próprio rótulo
// (data nua no meio da linha não diz de que data se trata).
const ROTULO_COM_DATA_RELATIVA = 'prazo'
// Relativos longos ("DD MMM", dia da semana) duplicariam a data ao lado.
const RELATIVOS_CURTOS = new Set(['Hoje', 'Ontem', 'Amanhã'])
const FORMATO_DO_PRAZO = 'DD/MM/YYYY'

function campoRotulado(parte) {
  const indiceDoisPontos = parte.indexOf(':')
  if (indiceDoisPontos === -1) return null
  return {
    rotulo: parte.slice(0, indiceDoisPontos).trim(),
    valor: parte.slice(indiceDoisPontos + 1).trim(),
  }
}

/**
 * Campos rotulados do resumo; `[]` com menos de 2 (cai no parágrafo único).
 * @example camposDoResumo('Criar tarefa · Título: X · Prazo: 01/01/2099') // [{rotulo:'Título',...}, {rotulo:'Prazo',...}]
 */
export function camposDoResumo(resumo) {
  if (!resumo) return []
  const campos = resumo
    .split(SEPARADOR_DO_RESUMO)
    .map((parte) => parte.trim())
    .filter(Boolean)
    .map(campoRotulado)
    .filter(Boolean)
  return campos.length > 1 ? campos : []
}

/** "hoje"/"ontem"/"amanhã" só para Prazo em `DD/MM/YYYY` válido; senão `''`. */
export function relativoDoCampo(chave, valor) {
  if (chave !== ROTULO_COM_DATA_RELATIVA) return ''
  const data = aawzDate(valor, FORMATO_DO_PRAZO)
  if (!data.isValid()) return ''
  const relativo = data.formatRelative()
  return RELATIVOS_CURTOS.has(relativo) ? relativo.toLowerCase() : ''
}

/**
 * TODOS os campos do resumo, decorados para a segunda linha da faixa.
 *
 * ★ [owner, 19/09/2026] O "Título" NÃO é mais içado para uma linha grande
 * própria: no mock ele é o PRIMEIRO item da linha de resumo ("Ligar para o
 * cliente 000123 · Prioridade alta · 02/09/2026"), e quem abre o cartão é o
 * título da AÇÃO ("Criar tarefa"), na linha de cima. Duas linhas de título
 * era o que fazia a faixa ter três alturas.
 *
 * Sem ícone por rótulo: o mock não tem nenhum, e o ponto de cor à esquerda já
 * é o único sinal gráfico da faixa.
 */
export function itensDoResumo(campos) {
  return campos.map((campo) => {
    const chave = campo.rotulo.toLowerCase()
    return {
      rotulo: campo.rotulo,
      valor: campo.valor,
      mostrarRotulo: chave === ROTULO_COM_DATA_RELATIVA,
      relativo: relativoDoCampo(chave, campo.valor),
    }
  })
}
